import styled from 'styled-components';
import { STATE_OPTIONS } from '../../../components/helpers';
import { Yup } from '../../../helpers/Yup';
import { validateCNS } from '../../../helpers/cns';
import { validateCPF } from '../../../helpers/cpf';
import { onlyDigits } from '../../../helpers/string';
import { DOCUMENT_TYPE } from '../../LME/helpers';
import { Icd } from '../../my-central/patientService';
import { ApacBpaRadioValue } from './Components/Radio';
import { DEFAULT_PRIMARY_COLOR } from '../../prescription/usePrescription/helpers.mock';
import { ReactComponent as CheckIcon } from '../../../assets/images/icons/mevo/check.svg';
import { Exam } from '../../../interfaces/exam';

export enum APAC_BPA_TABS {
  PATIENT = 0,
  PROFISSIONAL = 1,
  PRESCRIPTION = 2,
}

export const APAC_BPA_SUBMIT_EVENT = 'apac-bpa:submit';
export const APAC_BPA_EXAM_SUBMIT_EVENT = 'apac-bpa:exam-submit';

export const GENDERS = ['Masculino', 'Feminino'];
export const GENDER_OPTIONS: ApacBpaRadioValue[] = GENDERS.map((gender) => ({ label: gender, value: gender }));

export const PROFESSIONAL_DOCUMENT_TYPES: ApacBpaRadioValue[] = Object.values(DOCUMENT_TYPE).map((type) => ({
  label: type,
  value: type,
}));

// Form 1 - Patient

export interface ApacBpaPatientFormType {
  patientName: string;
  parentName?: string;
  parentPhone?: string;
  personInChargeName?: string;
  personInChargePhone?: string;
  ethnicity?: string;
  patientDocNumber: string;
  patientGender: string;
  medicalRecordNumber?: string;
  patiendBirthdate: string;
  patientRace?: string;
  patientCep: string;
  patientAddress: string;
  patientUf: string;
  patientCity: string;
  ibgeCityCode: string;
}

export const ApacBpaPatientFormSchema = {
  patientName: Yup.string().required('Informe um nome válido'),
  parentName: Yup.string().nullable(),
  parentPhone: Yup.string().nullable(),
  personInChargeName: Yup.string().nullable(),
  personInChargePhone: Yup.string().nullable(),
  ethnicity: Yup.string().nullable(),
  patientDocNumber: Yup.string().test('validate cns', 'Informe um CNS válido', function (patientDocNumber) {
    const document = onlyDigits(patientDocNumber || '');

    if (!document) return true;

    return validateCNS(document);
  }),
  patientGender: Yup.string().required('Selecione uma opção'),
  medicalRecordNumber: Yup.string().nullable(),
  patiendBirthdate: Yup.string().required('Informe uma data de nascimento válida'),
  patientRace: Yup.string().nullable(),
  patientCep: Yup.string().required('Informe um CEP válido'),
  patientAddress: Yup.string().required('Informe um endereço válido'),
  patientUf: Yup.string()
    .required('Informe uma UF válida')
    .test('validete uf', 'Informe uma UF válida', (uf) => {
      return STATE_OPTIONS.includes(uf);
    }),
  patientCity: Yup.string().required('Informe um município válido'),
  ibgeCityCode: Yup.string().nullable(),
};

// Form 2 - Professional

export interface ApacBpaProfessionalFormType {
  establishmentName: string;
  cnesNumber: string;
  professionalName: string;
  docType?: DOCUMENT_TYPE;
  professionalDocNumber: string;
  docDate: string;
}

export const ApacBpaProfessionalFormSchema = {
  establishmentName: Yup.string().required('Informe um nome de estabelecimento válido'),
  cnesNumber: Yup.string().required('Informe um CNES válido'),
  professionalName: Yup.string().required('Informe um nome válido'),
  docType: Yup.string().required('Selecione uma das opções'),
  professionalDocNumber: Yup.string()
    .required('Informe um documento válido')
    .when('docType', {
      is: (docType: string) => docType === DOCUMENT_TYPE.CPF,
      then: (schema) =>
        schema.test('validate cpf', 'Informe um CPF válido', function (patientDocNumber) {
          const document = onlyDigits(patientDocNumber || '');

          if (!document) return true;

          return validateCPF(document);
        }),
      otherwise: (schema) =>
        schema.test('validate cpf', 'Informe um CNS válido', function (patientDocNumber) {
          const document = onlyDigits(patientDocNumber || '');

          if (!document) return true;

          return validateCNS(document);
        }),
    }),
  docDate: Yup.string().required('Informe uma data de solicitação válida'),
};

// Form 3 - Prescription

export const APAC_MAX_SECONDARY_PROCEDURES = 5;
export const BPA_MAX_SECONDARY_PROCEDURES = 2;

export const EMPTY_APAC_BPA_EXAM = {
  procedure: {
    name: '',
    code: '',
    quantity: 0,
  },
  secondaryProcedures: [],
  CID10: null,
  CID10Related: null,
  CID10Secondary: null,
  examAnamnese: '',
  examDescription: '',
  examJustify: '',
};

export enum CID10_TYPES {
  primary = 'CID10',
  secondary = 'CID10Secondary',
  related = 'CID10Related',
}

export interface ApacBpaProcedure {
  name: string;
  code: string;
  quantity: number;
}

export enum ApacBpaExamTypeKey {
  APAC = 'FORMULARIO_APAC',
  BPA = 'FORMULARIO_BPA',
}

export interface ApacBpaExamType {
  id: number;
  key: ApacBpaExamTypeKey;
  name: string;
}

export interface ApacBpaPrescriptionExamFromType {
  formId?: number;
  exam?: Exam;
  procedure: ApacBpaProcedure;
  secondaryProcedures: ApacBpaProcedure[];
  [CID10_TYPES.primary]: Icd | null;
  examDescription: string;
  [CID10_TYPES.secondary]: Icd | null;
  [CID10_TYPES.related]: Icd | null;
  examAnamnese: string;
  examJustify: string;
  formType: ApacBpaExamType;
}

export interface ApacBpaPrescriptionFormType {
  exams: ApacBpaPrescriptionExamFromType[];
}

export const ApacBpaPrescriptionExamFormSchema = {
  procedure: Yup.object()
    .required('Informe os dados do procedimento')
    .shape({
      code: Yup.string()
        .required('Informe um código válido')
        .test('procedure-code', 'Informe um código válido', (code) => {
          return onlyDigits(code).length === 10;
        }),
      name: Yup.string().required('Informe o nome do procedimento'),
      quantity: Yup.number().required('Informe a quantidade').min(1, 'Informe uma quantidade válida'),
    }),
  secondaryProcedures: Yup.array().of(
    Yup.object().shape({
      code: Yup.string()
        .nullable()
        .test('procedure-code', 'Informe um código válido', (code, { parent }) => {
          if ((!!parent.name || !!parent.quantity) && !code) return false;
          if (!code) return true;
          return onlyDigits(code ?? '').length === 10;
        }),
      name: Yup.string().when('code', {
        is: (val: string) => !!val,
        then: (schema) => schema.required('Informe o nome do procedimento'),
      }),
      quantity: Yup.number()
        .min(1, 'Informe uma quantidade válida')
        .when('code', {
          is: (val: string) => !!val,
          then: (schema) => schema.required('Informe a quantidade'),
        })
        .when('name', {
          is: (val: string) => !!val,
          then: (schema) => schema.required('Informe a quantidade'),
        }),
    })
  ),
  [CID10_TYPES.primary]: Yup.object().nullable(),
  examDescription: Yup.string().required('Informe o diagnóstico'),
  [CID10_TYPES.secondary]: Yup.object().nullable(),
  [CID10_TYPES.related]: Yup.object().nullable(),
  examAnamnese: Yup.string().nullable(),
  examJustify: Yup.string().nullable(),
};

// API Interface

export interface ApacBpaApiSecondaryProcedurePayload {
  idPrescricaoExameFormularioProcedimentoSecundario?: number;
  idPrescricaoExameFormulario?: number;
  codigoExame: string;
  nome: string;
  quantidade: number;
}

export interface ApacBpaApiCidPayload {
  id: number;
  descricao: string;
  subCategoria: string;
}

export interface ApacBpaApiPrescriptionPayload {
  idPrescricaoExameFormulario?: number;
  idPrescricaoExame?: number;
  codigoExame: string;
  nome: string;
  quantidade: number;
  anamnese: string;
  exameJustificativaObservacao: string;
  tipoExameFormulario: {
    idTipoExameFormulario: number;
    Chave: string;
    Nome: string;
  };
  procedimentosSecundarios: ApacBpaApiSecondaryProcedurePayload[];
  cid?: ApacBpaApiCidPayload;
  cidSecundario?: ApacBpaApiCidPayload;
  cidAssociado?: ApacBpaApiCidPayload;
}

export interface ApacBpaApiPatient {
  idPrescricaoExameFormularioPaciente?: number;
  nome: string;
  sexo: string;
  nomeMae?: string;
  telefoneMae?: string;
  cns: string;
  numeroProntuario?: string;
  nomeResponsavel?: string;
  telefoneResponsavel?: string;
  dataNascimento: string;
  racaCor?: string;
  etnia?: string;
  endereco: {
    cep: string;
    endereco: string;
    uf: string;
    municipioDeResidencia: string;
    codigoIbgeMunicipio: string;
  };
}

export interface ApacBpaApiProfessinoal {
  idPrescricaoExameFormularioMedico?: number;
  nome: string;
  dataDaSolicitacao: string;
  documento: string;
  tipoDocumento: string;
}

export interface ApacBpaApiEstablishment {
  idPrescricaoExameFormularioEstabelecimento?: number;
  nome: string;
  cnes: string;
}

export interface ApacBpaApiPayload {
  exameFormularioPaciente: ApacBpaApiPatient;
  exameFormularioEstabelecimento: ApacBpaApiEstablishment;
  exameFormularioMedico: ApacBpaApiProfessinoal;
  exameFormularioPrescricao: ApacBpaApiPrescriptionPayload[];
}

// Styled Components

export const StyledCheckIcon = styled(CheckIcon)`
  ${(props) => `
    --primary-color: ${props.color || DEFAULT_PRIMARY_COLOR};
  `}

  /* Apply styles to specific paths within the CheckIcon */
  & path:nth-of-type(2),
  & path:nth-of-type(3) {
    fill: var(--primary-color) !important; /* Use the defined variable */
  }
`;
